/**
 * Automatic sales-order charges.
 *
 * Reset magnets (mode `auto`): one free magnet per family, quantity
 * max(1, floor(fault-indicator quantity / 50)). Families: FI-3C -> MG-3C-2,
 * FI-5A -> MG-5A, FI-5A-W -> MG-5A-W. Fiber cables, test kits, TLA, and
 * magnet SKUs do not count.
 *
 * Skip automatic magnets when the mode is waive, the customer PO is an RMA,
 * the end user is exempt for that family, or the order is a stocking order.
 * Local and Regional billed customers are always stocking orders. Non-Stocking
 * never is. Mixed is a stocking order only when the end user is that same
 * customer, or a location of that customer (for example Stuart C Irby Company
 * and Stuart C Irby - Burnsville).
 *
 * User-entered magnet lines stay as typed (`origin` user). The free magnet is
 * its own line (`origin` auto-magnet). Each pass rebuilds that automatic line
 * from the user lines, so opening the order replaces it instead of adding another.
 * Waive removes the automatic line from the saved order. The editor keeps a
 * restore banner. PDFs, batches, and invoices only see saved lines.
 *
 * Shipping (mode `auto`): $20 under $5000 merchandise for a contiguous US
 * ship-to, unless the client is shipping-fee exempt or merchandise is $0.
 */
export const SALES_ORDER_SHIPPING_FREE_THRESHOLD_USD = 5000;

export type SalesOrderChargeMode = "auto" | "include" | "waive";

export type MagnetFamily = "mg_3c_2" | "mg_5a" | "mg_5a_w";

export const MAGNET_SKU_BY_FAMILY: Record<MagnetFamily, string> = {
  mg_3c_2: "MG-3C-2",
  mg_5a: "MG-5A",
  mg_5a_w: "MG-5A-W",
};

const MAGNET_SKU_FAMILY: Record<string, MagnetFamily> = {
  "MG-3C-2": "mg_3c_2",
  "MG-5A": "mg_5a",
  "MG-5A-W": "mg_5a_w",
};

const NON_CONTIGUOUS_US_REGIONS = new Set([
  "AK",
  "ALASKA",
  "HI",
  "HAWAII",
  "PR",
  "PUERTO RICO",
  "GU",
  "GUAM",
  "VI",
  "VIRGIN ISLANDS",
  "USVI",
  "US VIRGIN ISLANDS",
  "AS",
  "AMERICAN SAMOA",
  "MP",
  "CNMI",
  "NORTHERN MARIANA",
  "NORTHERN MARIANA ISLANDS",
]);

export type ChargeLine = {
  sku: string;
  quantity: number;
  unitPrice: number;
  productDescription: string | null;
  remark: string | null;
  origin: "user" | "auto-shipping" | "auto-magnet";
};

export function parseChargeLineOrigin(value: unknown): ChargeLine["origin"] | null {
  return value === "user" || value === "auto-shipping" || value === "auto-magnet" ? value : null;
}

/**
 * Classifies persisted lines that were saved before `charge_origin` existed.
 * Lines that are not magnet or shipping SKUs are user lines. Those user lines
 * are fed through `applyAutomaticChargeLines`; a magnet or shipping line is
 * tagged automatic only when the order would have received it for free with
 * the same SKU and quantity. Everything else stays a user line.
 */
export function classifyLegacyChargeLineOrigins(
  args: Omit<Parameters<typeof applyAutomaticChargeLines>[0], "items"> & {
    items: Array<Omit<ChargeLine, "origin">>;
  },
): Array<ChargeLine["origin"]> {
  const origins: Array<ChargeLine["origin"]> = args.items.map(() => "user");
  const candidateIndexes = new Set<number>();
  args.items.forEach((item, index) => {
    if (magnetFamilyForMagnetSku(item.sku) != null || isShippingSku(item.sku, args.shippingSku)) {
      candidateIndexes.add(index);
    }
  });
  if (candidateIndexes.size === 0) {
    return origins;
  }
  const userItems: ChargeLine[] = args.items
    .filter((_, index) => !candidateIndexes.has(index))
    .map((item) => ({ ...item, origin: "user" }));
  const expected = applyAutomaticChargeLines({ ...args, items: userItems }).filter(
    (item) => item.origin !== "user",
  );
  for (const auto of expected) {
    const sku = auto.sku.trim().toUpperCase();
    const matchIndex = args.items.findIndex(
      (item, index) =>
        candidateIndexes.has(index) &&
        origins[index] === "user" &&
        item.sku.trim().toUpperCase() === sku &&
        (auto.origin === "auto-shipping" || (item.quantity === auto.quantity && item.unitPrice === 0)),
    );
    if (matchIndex >= 0) {
      origins[matchIndex] = auto.origin;
    }
  }
  return origins;
}

export function parseSalesOrderChargeMode(value: unknown): SalesOrderChargeMode {
  if (value == null || value === "") {
    return "auto";
  }
  if (value === "auto" || value === "include" || value === "waive") {
    return value;
  }
  throw new Error("Charge mode must be auto, include, or waive.");
}

/** Callers that can edit the order persist requested auto/include/waive modes. */
export function resolveEditableSalesOrderChargeModes(args: {
  canOverride: boolean;
  requestedShipping: unknown;
  requestedMagnet: unknown;
  storedShipping?: unknown;
  storedMagnet?: unknown;
}): { shippingMode: SalesOrderChargeMode; magnetMode: SalesOrderChargeMode } {
  if (args.canOverride) {
    return {
      shippingMode: parseSalesOrderChargeMode(args.requestedShipping),
      magnetMode: parseSalesOrderChargeMode(args.requestedMagnet),
    };
  }
  return {
    shippingMode: parseSalesOrderChargeMode(args.storedShipping),
    magnetMode: parseSalesOrderChargeMode(args.storedMagnet),
  };
}

export function isShippingSku(sku: string, configuredSku: string): boolean {
  return sku.trim().toLowerCase() === configuredSku.trim().toLowerCase();
}

function normalizePlace(value: string | null | undefined): string {
  return (value ?? "")
    .trim()
    .toUpperCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ");
}

export function isUnitedStatesCountry(country: string | null | undefined): boolean {
  const normalized = normalizePlace(country);
  return (
    normalized === "" ||
    normalized === "US" ||
    normalized === "USA" ||
    normalized === "UNITED STATES" ||
    normalized === "UNITED STATES OF AMERICA"
  );
}

export function isContiguousUnitedStatesShipTo(
  country: string | null | undefined,
  state: string | null | undefined,
): boolean {
  if (!isUnitedStatesCountry(country)) {
    return false;
  }
  const stateNorm = normalizePlace(state);
  if (stateNorm !== "" && NON_CONTIGUOUS_US_REGIONS.has(stateNorm)) {
    return false;
  }
  return true;
}

export function normalizeStockingValue(stocking: string | null | undefined): string {
  return (stocking ?? "").trim().toLowerCase();
}

/** Local and Regional always stock. Mixed depends on the end user. Non-Stocking never stocks. */
export function isStockingOrderCustomer(stocking: string | null | undefined): boolean {
  const normalized = normalizeStockingValue(stocking);
  return normalized === "local" || normalized === "regional";
}

function accountIdKey(value: string | number | null | undefined): string | null {
  if (value == null) {
    return null;
  }
  const text = String(value).trim();
  return text === "" ? null : text;
}

/** Compare account names ignoring case, punctuation, and a trailing "Company". */
export function stockingAccountNameKey(value: string | null | undefined): string {
  return (value ?? "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/ company$/, "");
}

/**
 * True when the end user name is the billed customer, or a location of that
 * customer: "Stuart C Irby Company" and "Stuart C Irby - Burnsville".
 */
export function endUserIsClientOrLocation(clientNames: Array<string | null | undefined>, endUserNames: Array<string | null | undefined>): boolean {
  const clients = clientNames.map(stockingAccountNameKey).filter((name) => name.length >= 4);
  const endUsers = endUserNames.map(stockingAccountNameKey).filter((name) => name.length > 0);
  for (const clientName of clients) {
    for (const endUserName of endUsers) {
      if (endUserName === clientName || endUserName.startsWith(`${clientName} `)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * True when automatic reset magnets should be skipped.
 * Local and Regional are always stocking orders. Non-Stocking never is.
 * Mixed is a stocking order when the end user is the billed customer or a location of that customer.
 */
export function isStockingReplenishmentOrder(args: {
  clientStocking: string | null | undefined;
  clientFsAccountId?: string | number | null;
  endUserFsAccountId?: string | number | null;
  clientNames?: Array<string | null | undefined>;
  endUserNames?: Array<string | null | undefined>;
}): boolean {
  const stocking = normalizeStockingValue(args.clientStocking);
  if (stocking === "local" || stocking === "regional") {
    return true;
  }
  if (stocking !== "mixed") {
    return false;
  }
  const clientId = accountIdKey(args.clientFsAccountId);
  const endUserId = accountIdKey(args.endUserFsAccountId);
  if (clientId != null && endUserId != null && clientId === endUserId) {
    return true;
  }
  return endUserIsClientOrLocation(args.clientNames ?? [], args.endUserNames ?? []);
}

/** Same Megaventory RMA reference pattern as `parseRmaNumberFromMegaventoryReference`. */
const RMA_CUSTOMER_PO_REGEX = /RMA[\s-]*(\d{4})-(\d{2})-(\d{2,3})\b/i;

export function isRmaReplacementCustomerPurchaseOrder(value: string | null | undefined): boolean {
  return RMA_CUSTOMER_PO_REGEX.test((value ?? "").trim());
}

export function isFiberCableSku(sku: string): boolean {
  const normalized = sku.trim().toUpperCase();
  return /^FI-3C\s+OC\b/.test(normalized) || /^FI-3C\s+PH\b/.test(normalized);
}

export function isFaultIndicatorTestKitSku(sku: string): boolean {
  return /(?:^|[\s-])TS(?:3X?|6)(?:$|[\s-])/i.test(sku.trim());
}

export function magnetFamilyForProductSku(sku: string): MagnetFamily | null {
  const normalized = sku.trim().toUpperCase();
  if (normalized === "") {
    return null;
  }
  if (MAGNET_SKU_FAMILY[normalized]) {
    return null;
  }
  if (normalized.endsWith(" MG") || normalized.endsWith("-MG")) {
    return null;
  }
  if (isFiberCableSku(normalized) || isFaultIndicatorTestKitSku(normalized)) {
    return null;
  }
  if (/(?:^|[\s-])TLA(?:$|[\s-])/.test(normalized)) {
    return null;
  }
  if (normalized.startsWith("FI-5A-W")) {
    return "mg_5a_w";
  }
  if (normalized.startsWith("FI-5A")) {
    return "mg_5a";
  }
  if (normalized.startsWith("FI-3C")) {
    return "mg_3c_2";
  }
  return null;
}

export function magnetFamilyForMagnetSku(sku: string): MagnetFamily | null {
  return MAGNET_SKU_FAMILY[sku.trim().toUpperCase()] ?? null;
}

/** Megaventory catalog name: description, then variant, joined by one space. */
export function formatMagnetCatalogDescription(
  description: string | null | undefined,
  variant: string | null | undefined,
): string {
  const name = description?.trim() ?? "";
  const detail = variant?.trim() ?? "";
  if (name !== "" && detail !== "") {
    return `${name} ${detail}`;
  }
  return name !== "" ? name : detail;
}

export function isPlaceholderMagnetDescription(description: string | null | undefined, sku: string): boolean {
  const trimmed = description?.trim() ?? "";
  return trimmed === "" || trimmed.toUpperCase() === sku.trim().toUpperCase();
}

/** Keep a typed description. Replace a blank or SKU-only description with the catalog name. */
export function resolveAutomaticMagnetDescription(args: {
  sku: string;
  previousDescription: string | null;
  catalogDescription: string | null;
}): string {
  const previous = args.previousDescription?.trim() ?? "";
  if (!isPlaceholderMagnetDescription(previous, args.sku)) {
    return previous;
  }
  const catalog = args.catalogDescription?.trim() ?? "";
  if (!isPlaceholderMagnetDescription(catalog, args.sku)) {
    return catalog;
  }
  return previous || catalog || args.sku;
}

function exemptionCoversFamily(exemptions: string[], family: MagnetFamily): boolean {
  if (family === "mg_3c_2") {
    return exemptions.includes("mg_3c_2");
  }
  return exemptions.includes("mg_5a");
}

export function magnetQuantityForFaultIndicatorCount(quantity: number): number {
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return 0;
  }
  return Math.max(1, Math.floor(quantity / 50));
}

export function shouldChargeShipping(args: {
  merchandiseTotal: number;
  mode: SalesOrderChargeMode;
  customerShippingFeeExempt?: boolean;
  shipToCountry?: string | null;
  shipToState?: string | null;
}): boolean {
  if (args.mode === "waive") {
    return false;
  }
  if (args.mode === "include") {
    return true;
  }
  if (args.merchandiseTotal <= 0 || args.merchandiseTotal >= SALES_ORDER_SHIPPING_FREE_THRESHOLD_USD) {
    return false;
  }
  if (args.customerShippingFeeExempt === true) {
    return false;
  }
  if (!isContiguousUnitedStatesShipTo(args.shipToCountry, args.shipToState)) {
    return false;
  }
  return true;
}

export function magnetChargesForItems(args: {
  items: Array<{ sku: string; quantity: number }>;
  exemptions: string[];
  mode: SalesOrderChargeMode;
  stockingOrder?: boolean;
  rmaOrder?: boolean;
}): Array<{ family: MagnetFamily; quantity: number }> {
  if (args.mode === "waive") {
    return [];
  }
  if (args.mode === "auto" && (args.stockingOrder === true || args.rmaOrder === true)) {
    return [];
  }

  const totals: Record<MagnetFamily, number> = {
    mg_3c_2: 0,
    mg_5a: 0,
    mg_5a_w: 0,
  };
  for (const item of args.items) {
    const family = magnetFamilyForProductSku(item.sku);
    if (family == null) {
      continue;
    }
    totals[family] += item.quantity;
  }

  const charges: Array<{ family: MagnetFamily; quantity: number }> = [];
  for (const family of Object.keys(totals) as MagnetFamily[]) {
    const magnetQuantity = magnetQuantityForFaultIndicatorCount(totals[family]);
    if (magnetQuantity <= 0) {
      continue;
    }
    if (args.mode === "auto" && exemptionCoversFamily(args.exemptions, family)) {
      continue;
    }
    charges.push({ family, quantity: magnetQuantity });
  }
  return charges;
}

export function merchandiseTotal(items: Array<{ sku: string; quantity: number; unitPrice: number }>, shippingSku: string): number {
  return items.reduce((sum, item) => {
    if (isShippingSku(item.sku, shippingSku)) {
      return sum;
    }
    return sum + item.quantity * item.unitPrice;
  }, 0);
}

export function applyAutomaticChargeLines(args: {
  items: ChargeLine[];
  shippingSku: string;
  shippingQuantity: number;
  shippingUnitPrice: number;
  shippingDescription: string;
  magnetCatalog: Array<{ sku: string; description: string; unitPrice: number }>;
  exemptions: string[];
  shippingMode: SalesOrderChargeMode;
  magnetMode: SalesOrderChargeMode;
  customerShippingFeeExempt?: boolean;
  shipToCountry?: string | null;
  shipToState?: string | null;
  stockingOrder?: boolean;
  rmaOrder?: boolean;
  customerPurchaseOrderNumber?: string | null;
}): ChargeLine[] {
  const userItems = args.items.filter((item) => item.origin === "user");
  const total = merchandiseTotal(userItems, args.shippingSku);
  const rmaOrder =
    args.rmaOrder === true || isRmaReplacementCustomerPurchaseOrder(args.customerPurchaseOrderNumber);
  const chargeShipping = shouldChargeShipping({
    merchandiseTotal: total,
    mode: args.shippingMode,
    customerShippingFeeExempt: args.customerShippingFeeExempt,
    shipToCountry: args.shipToCountry,
    shipToState: args.shipToState,
  });
  const magnetCharges = magnetChargesForItems({
    items: userItems,
    exemptions: args.exemptions,
    mode: args.magnetMode,
    stockingOrder: args.stockingOrder,
    rmaOrder,
  });

  const next: ChargeLine[] = [...userItems];

  for (const charge of magnetCharges) {
    const sku = MAGNET_SKU_BY_FAMILY[charge.family];
    const previous = args.items.find((item) => item.origin === "auto-magnet" && item.sku.trim().toUpperCase() === sku);
    const catalog = args.magnetCatalog.find((option) => option.sku.trim().toUpperCase() === sku);
    next.push({
      sku,
      quantity: charge.quantity,
      unitPrice: previous != null ? previous.unitPrice : 0,
      productDescription: resolveAutomaticMagnetDescription({
        sku,
        previousDescription: previous?.productDescription ?? null,
        catalogDescription: catalog?.description ?? null,
      }),
      remark: previous?.remark ?? null,
      origin: "auto-magnet",
    });
  }

  if (chargeShipping && !userItems.some((item) => isShippingSku(item.sku, args.shippingSku))) {
    const previous = args.items.find((item) => item.origin === "auto-shipping");
    next.push({
      sku: args.shippingSku,
      quantity: previous != null && previous.quantity > 0 ? previous.quantity : args.shippingQuantity,
      unitPrice: previous != null && Number.isFinite(previous.unitPrice) ? previous.unitPrice : args.shippingUnitPrice,
      productDescription: previous?.productDescription ?? args.shippingDescription,
      remark: previous?.remark ?? null,
      origin: "auto-shipping",
    });
  }

  return next;
}
